<template>
    <div class="qi-page">
        <div class="qi-page-header d-flex justify-content-between align-items-center flex-wrap" style="gap:8px;">
            <div>
                <h4 class="mb-0 text-primary">Loyalty</h4>
                <small class="text-muted">Members, points earned/redeemed, and outstanding liability for your loyalty program.</small>
            </div>
            <div class="d-flex align-items-center" style="gap:8px;">
                <date-range-picker :auto-apply="true" :ranges="false" :append-to-body="true" v-model="dateRangePicker" style="width:220px;">
                    <template #input="picker">
                        <span v-if="picker.startDate && picker.endDate">{{ formatPickerDate(picker.startDate) }} - {{ formatPickerDate(picker.endDate) }}</span>
                        <span v-else class="text-muted small"><i class="bi bi-calendar-event mr-1"></i>Date range</span>
                    </template>
                </date-range-picker>
            </div>
        </div>

        <div v-if="loading" class="text-center py-5"><span class="spinner-border"></span></div>

        <div v-else-if="!hasEntitlement" class="empty-state">
            <i class="bi bi-star" style="font-size:2rem; opacity:.3;"></i>
            <p class="text-muted mt-2">Loyalty is not enabled for this store.
                <a href="/settings/features">Enable it in Settings → Features</a>.</p>
        </div>

        <template v-else-if="summary">
            <div class="row mb-3">
                <div class="col-6 col-md-3 mb-3">
                    <div class="stat-card">
                        <div class="stat-card__lbl">Members</div>
                        <div class="stat-card__val">{{ summary.members }}</div>
                        <div class="stat-card__sub">{{ summary.active_members }} active this period</div>
                    </div>
                </div>
                <div class="col-6 col-md-3 mb-3">
                    <div class="stat-card">
                        <div class="stat-card__lbl">Points earned</div>
                        <div class="stat-card__val">{{ summary.points_earned }}</div>
                        <div class="stat-card__sub">this period</div>
                    </div>
                </div>
                <div class="col-6 col-md-3 mb-3">
                    <div class="stat-card">
                        <div class="stat-card__lbl">Points redeemed</div>
                        <div class="stat-card__val">{{ summary.points_redeemed }}</div>
                        <div class="stat-card__sub">this period</div>
                    </div>
                </div>
                <div class="col-6 col-md-3 mb-3">
                    <div class="stat-card stat-card--danger">
                        <div class="stat-card__lbl">Points outstanding</div>
                        <div class="stat-card__val">{{ summary.points_outstanding }}</div>
                        <div class="stat-card__sub">total liability, all time</div>
                    </div>
                </div>
                <div class="col-6 col-md-4 mb-3">
                    <div class="stat-card">
                        <div class="stat-card__lbl">Redemption value</div>
                        <div class="stat-card__val">{{ formatMoney(summary.redemption_value) }}</div>
                        <div class="stat-card__sub">discount given this period</div>
                    </div>
                </div>
                <div class="col-6 col-md-4 mb-3">
                    <div class="stat-card">
                        <div class="stat-card__lbl">Sales with loyalty</div>
                        <div class="stat-card__val">{{ summary.sales_with_loyalty }}</div>
                        <div class="stat-card__sub">orders this period</div>
                    </div>
                </div>
                <div class="col-6 col-md-4 mb-3">
                    <div class="stat-card">
                        <div class="stat-card__lbl">Points expired</div>
                        <div class="stat-card__val">{{ summary.points_expired }}</div>
                        <div class="stat-card__sub">expiry not active yet</div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import moment from 'moment';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';

export default {
    name: 'LoyaltyReport',
    components: { DateRangePicker },
    data() {
        const today = new Date();
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
            loading: true,
            hasEntitlement: true,
            summary: null,
            startDate: monthStart.toISOString().slice(0, 10),
            endDate: today.toISOString().slice(0, 10),
        };
    },
    created() {
        this.load();
    },
    computed: {
        dateRangePicker: {
            get() {
                return {
                    startDate: this.startDate ? new Date(this.startDate) : null,
                    endDate: this.endDate ? new Date(this.endDate) : null,
                };
            },
            set(val) {
                this.startDate = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : '';
                this.endDate = val && val.endDate ? moment(val.endDate).format('YYYY-MM-DD') : '';
                this.load();
            },
        },
    },
    methods: {
        formatPickerDate(d) {
            if (!d) return '';
            return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        },
        async load() {
            this.loading = true;
            try {
                const { data } = await axios.get('/api/loyalty/reports/summary', {
                    params: { start_date: this.startDate, end_date: this.endDate },
                });
                this.summary = data;
                this.hasEntitlement = true;
            } catch (e) {
                if (e.response && e.response.status === 403) {
                    this.hasEntitlement = false;
                } else {
                    this.$toasts.push({ type: 'error', message: 'Could not load the loyalty report.' });
                }
            } finally {
                this.loading = false;
            }
        },
        formatMoney(v) {
            return 'KES ' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
    },
};
</script>

<style scoped>
.qi-page { padding: 16px; max-width: 1300px; margin: 0 auto; }
@media (max-width: 768px) { .qi-page { padding-bottom: 90px; } }
.qi-page-header { margin-bottom: 16px; }
.stat-card { border: 1px solid #eee; border-radius: 8px; padding: 14px; text-align: center; background: #fff; }
.stat-card--danger { border-color: #f5c6cb; }
.stat-card__lbl { font-size: 11px; text-transform: uppercase; letter-spacing: .5px; color: #6c757d; }
.stat-card__val { font-size: 20px; font-weight: 700; margin: 4px 0 2px; }
.stat-card__sub { font-size: 11px; color: #999; }
.empty-state { text-align: center; padding: 48px 16px; }
</style>
